<!-- CustomSlider.vue -->
<template>
  <div class="custom-slider-wrapper">
    <div class="values">
      <span class="min-value">{{ formatValue(modelValue[0]) }}</span>
      <span class="separator">|</span>
      <span class="max-value">{{ formatValue(modelValue[1]) }}</span>
    </div>
    <div ref="slider" class="slider-container">
      <div class="slider-track" @mousedown="onTrackClick">
        <div class="slider-progress" :style="progressStyle"></div>
      </div>
      <div
        class="slider-thumb"
        :style="{ left: minPosition + '%' }"
        @mousedown="startDrag($event, 'min')"
        @touchstart="startDrag($event, 'min')"
      ></div>
      <div
        class="slider-thumb"
        :style="{ left: maxPosition + '%' }"
        @mousedown="startDrag($event, 'max')"
        @touchstart="startDrag($event, 'max')"
      ></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CustomSlider",
  props: {
    modelValue: Array,
    min: Number,
    max: Number,
    format: {
      type: Function,
      default: (value) => value,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      activeThumb: null,
    };
  },
  computed: {
    minPosition() {
      return ((this.modelValue[0] - this.min) / (this.max - this.min)) * 100;
    },
    maxPosition() {
      return ((this.modelValue[1] - this.min) / (this.max - this.min)) * 100;
    },
    progressStyle() {
      return {
        left: this.minPosition + "%",
        width: this.maxPosition - this.minPosition + "%",
      };
    },
  },
  mounted() {
    document.addEventListener("mousemove", this.onDrag);
    document.addEventListener("mouseup", this.stopDrag);
    document.addEventListener("touchmove", this.onDrag, { passive: false });
    document.addEventListener("touchend", this.stopDrag);
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchmove", this.onDrag);
    document.removeEventListener("touchend", this.stopDrag);
  },
  methods: {
    formatValue(value) {
      return this.format(value);
    },
    startDrag(event, thumb) {
      this.activeThumb = thumb;
      event.preventDefault();
    },
    onDrag(event) {
      if (!this.activeThumb) return;

      const slider = this.$refs.slider;
      const rect = slider.getBoundingClientRect();
      let clientX;
      if (event.type.startsWith("touch")) {
        clientX = event.touches[0].clientX;
      } else {
        clientX = event.clientX;
      }

      const x = clientX - rect.left;
      let percent = (x / rect.width) * 100;
      percent = Math.max(0, Math.min(100, percent));

      const value = this.min + (percent / 100) * (this.max - this.min);
      const newValue = [...this.modelValue];

      if (this.activeThumb === "min") {
        newValue[0] = Math.max(this.min, Math.min(value, newValue[1]));
      } else {
        newValue[1] = Math.max(newValue[0], Math.min(value, this.max));
      }

      this.$emit("update:modelValue", newValue);

      if (event.cancelable) event.preventDefault();
    },
    stopDrag() {
      this.activeThumb = null;
    },
    onTrackClick(event) {
      const slider = this.$refs.slider;
      const rect = slider.getBoundingClientRect();
      const x = event.clientX - rect.left;
      let percent = (x / rect.width) * 100;
      percent = Math.max(0, Math.min(100, percent));

      const value = this.min + (percent / 100) * (this.max - this.min);
      const newValue = [...this.modelValue];

      const distToMin = Math.abs(value - newValue[0]);
      const distToMax = Math.abs(value - newValue[1]);

      if (distToMin < distToMax) {
        newValue[0] = Math.max(this.min, Math.min(value, newValue[1]));
      } else {
        newValue[1] = Math.max(newValue[0], Math.min(value, this.max));
      }

      this.$emit("update:modelValue", newValue);
    },
  },
};
</script>

<style scoped>
.custom-slider-wrapper {
  min-width: 178px;
  width: 100%;
  height: 56px;
  border: 1px solid #d3d9d3;
  border-radius: 56px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.values {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #545863;
  position: relative;
}

.min-value,
.max-value {
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  display: inline-block;
}

.min-value {
  text-align: left;
}

.max-value {
  text-align: right;
}

.separator {
  position: absolute;
  left: 50%;
  transform: translate(-50%);
  font-size: 16px;
  font-weight: 500;
  flex-shrink: 0;
  color: #28291f1f;
}

.slider-container {
  position: relative;
  top: 19px;
  height: 20px;
  cursor: pointer;
}

.slider-track {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-radius: 2px;
}

.slider-progress {
  position: absolute;
  height: 2px;
  background: #ff0020;
  border-radius: 2px;
}

.slider-thumb {
  position: absolute;
  width: 13px;
  height: 13px;
  background: #ff0020;
  border-radius: 50%;
  bottom: -3px;
  transform: translateX(-50%);
  cursor: grab;
}

.slider-thumb:active {
  cursor: grabbing;
}
@media (max-width: 768px) {
  .custom-slider-wrapper {
    padding: 21px 15px 19px 15px;
    min-width: auto;
  }
  .slider-container {
    top: 17px;
  }
}
</style>
